import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { Plus, Pencil, Trash2, X, Save } from 'lucide-react';

const emptyForm = { nome: '', email: '', senha: '', role: 'operador', is_ativo: true };

export default function Usuarios() {
  const { user: currentUser } = useAuth();
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = () => api.get('/configuracoes/usuarios').then(r => setUsuarios(r.data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(emptyForm); setEditId(null); setShowForm(true); setError(''); };
  const openEdit = (u) => { setForm({ nome: u.nome, email: u.email, senha: '', role: u.role, is_ativo: u.is_ativo }); setEditId(u.id); setShowForm(true); setError(''); };

  const handleSave = async () => {
    if (!form.nome || !form.email) { setError('Nome e e-mail são obrigatórios'); return; }
    if (!editId && !form.senha) { setError('Senha é obrigatória para novo usuário'); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/configuracoes/usuarios/${editId}`, form);
      else await api.post('/configuracoes/usuarios', form);
      setShowForm(false);
      setLoading(true);
      load();
    } catch (err) { setError(err.userMessage || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Inativar este usuário?')) return;
    try {
      await api.delete(`/configuracoes/usuarios/${id}`);
      setUsuarios(us => us.filter(u => u.id !== id));
    } catch (err) { alert(err.userMessage || 'Erro'); }
  };

  const inputCls = "w-full rounded-lg border border-brand-border px-3 py-2 text-sm font-body focus:outline-none focus:ring-2 focus:ring-brand-accent-mid bg-white";

  return (
    <div data-testid="usuarios-page">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="dashboard-heading text-2xl">Usuários do Sistema</h1><p className="text-brand-text-muted font-body text-sm">Administradores e Operadores do Dashboard</p></div>
        <button onClick={openNew} className="flex items-center gap-2 py-2 px-4 rounded-lg btn-primary font-dashboard font-semibold text-sm" data-testid="new-user-btn"><Plus className="w-4 h-4" /> Novo Usuário</button>
      </div>

      {showForm && (
        <div className="card-dashboard mb-4 max-w-md">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-dashboard font-semibold text-brand-text">{editId ? 'Editar' : 'Novo'} Usuário</h3>
            <button onClick={() => setShowForm(false)}><X className="w-4 h-4 text-brand-text-muted" /></button>
          </div>
          {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
          <div className="space-y-3">
            <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Nome *</label><input className={inputCls} value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))} data-testid="field-nome-user" /></div>
            <div><label className="block text-xs text-brand-text-muted mb-1 font-body">E-mail *</label><input type="email" className={inputCls} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="field-email-user" /></div>
            <div><label className="block text-xs text-brand-text-muted mb-1 font-body">{editId ? 'Nova Senha (deixe em branco para manter)' : 'Senha *'}</label><input type="password" className={inputCls} value={form.senha} onChange={e => setForm(f => ({ ...f, senha: e.target.value }))} placeholder="Mín. 8 caracteres" /></div>
            <div><label className="block text-xs text-brand-text-muted mb-1 font-body">Perfil</label>
              <select className={inputCls} value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} data-testid="select-role">
                <option value="operador">Operador</option>
                <option value="admin">Administrador</option>
              </select>
            </div>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={form.is_ativo} onChange={e => setForm(f => ({ ...f, is_ativo: e.target.checked }))} /><span className="text-sm font-body text-brand-text">Ativo</span></label>
            <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 rounded-lg btn-primary font-dashboard font-semibold text-sm" data-testid="save-user-btn"><Save className="w-4 h-4" /> {saving ? 'Salvando...' : 'Salvar'}</button>
          </div>
        </div>
      )}

      <div className="card-dashboard">
        {loading ? <div className="flex justify-center py-12"><div className="w-6 h-6 border-4 border-brand-primary border-t-transparent rounded-full animate-spin" /></div> :
          usuarios.length === 0 ? <p className="text-center text-brand-text-muted text-sm py-12 font-body">Nenhum usuário</p> : (
          <table className="w-full text-sm font-body">
            <thead><tr className="border-b border-brand-border bg-brand-bg">
              <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Nome</th>
              <th className="text-left py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">E-mail</th>
              <th className="text-center py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Perfil</th>
              <th className="text-center py-2.5 px-3 text-xs text-brand-text-muted font-dashboard">Status</th>
              <th className="py-2.5 px-3"></th>
            </tr></thead>
            <tbody>
              {usuarios.map(u => (
                <tr key={u.id} className="border-b border-brand-border last:border-0 hover:bg-brand-bg/50" data-testid="user-row">
                  <td className="py-2.5 px-3 font-medium text-brand-text">{u.nome} {u.id === currentUser?.id && <span className="text-xs text-brand-text-muted">(você)</span>}</td>
                  <td className="py-2.5 px-3 text-brand-text-muted">{u.email}</td>
                  <td className="py-2.5 px-3 text-center"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.role === 'admin' ? 'bg-brand-accent-light text-brand-dark' : 'bg-blue-100 text-blue-700'}`}>{u.role === 'admin' ? 'Admin' : 'Operador'}</span></td>
                  <td className="py-2.5 px-3 text-center"><span className={`text-xs px-2 py-0.5 rounded-full font-medium ${u.is_ativo ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{u.is_ativo ? 'Ativo' : 'Inativo'}</span></td>
                  <td className="py-2.5 px-3">
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => openEdit(u)} className="p-1.5 rounded hover:bg-brand-bg" data-testid="edit-user-btn"><Pencil className="w-3.5 h-3.5 text-brand-text-muted" /></button>
                      {u.id !== currentUser?.id && <button onClick={() => handleDelete(u.id)} className="p-1.5 rounded hover:bg-red-50" data-testid="delete-user-btn"><Trash2 className="w-3.5 h-3.5 text-red-500" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
